import React, { useState, useRef } from 'react';
import { uploadImage, validateImage, resizeImage } from '../services/imageService';
import ImageCrop from './ImageCrop';

const ImageUpload = ({ currentImage, onImageUpload, label, folder = 'images', enableCrop = true, aspectRatio = null, previewComponent = null }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(currentImage);
    const [error, setError] = useState('');
    const [showCrop, setShowCrop] = useState(false);
    const [originalFile, setOriginalFile] = useState(null);
    const [cropSrc, setCropSrc] = useState('');
    const fileInputRef = useRef(null);

    // Calculate aspect ratio based on folder
    const getAspectRatio = () => {
        if (aspectRatio) return aspectRatio;
        if (folder === 'hero') {
            const viewportWidth = window.innerWidth;
            const heroHeight = Math.min(Math.max(window.innerHeight * 0.7, 600), 800);
            return viewportWidth / heroHeight;
        }
        return 16 / 9;
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setError('');

            // Validate the file
            validateImage(file);

            setOriginalFile(file);

            if (enableCrop) {
                // Show crop interface
                const previewUrl = URL.createObjectURL(file);
                setCropSrc(previewUrl);
                setShowCrop(true);
            } else {
                // Upload directly without cropping
                await uploadImageFile(file);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleCropComplete = async (croppedBlob) => {
        try {
            setShowCrop(false);

            // Create a new File object from the cropped blob
            const croppedFile = new File([croppedBlob], originalFile.name, {
                type: croppedBlob.type || originalFile.type,
                lastModified: Date.now()
            });

            await uploadImageFile(croppedFile);
        } catch (error) {
            setError(error.message);
        }
    };

    const uploadImageFile = async (file) => {
        try {
            setUploading(true);

            // Create preview
            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);

            // For cropped images, skip resizing to preserve quality
            // Only resize if the file is extremely large
            let finalFile = file;
            if (file.size > 10 * 1024 * 1024) { // Only resize if larger than 10MB
                const resizedFile = await resizeImage(file, 2400, 1600, 0.95); // Higher resolution and quality
                finalFile = new File([resizedFile], file.name, {
                    type: resizedFile.type || file.type,
                    lastModified: Date.now()
                });
            } else {
                finalFile = new File([file], file.name, {
                    type: file.type,
                    lastModified: Date.now()
                });
            }

            // Upload to Firebase
            const result = await uploadImage(finalFile, folder);

            // Call parent callback with the new image URL
            onImageUpload(result.url, result.path);

            console.log('Image uploaded successfully:', result.url);
        } catch (error) {
            setError(error.message);
            setPreview(currentImage); // Revert to original image
        } finally {
            setUploading(false);
            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveImage = () => {
        setPreview(null);
        onImageUpload(null, null);
    };

    const handleCropCancel = () => {
        setShowCrop(false);
        setCropSrc('');
        setOriginalFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-4">
            <label className="block text-sm font-medium text-stone-700">
                {label}
            </label>

            {/* Image Preview */}
            <div className="relative">
                {preview ? (
                    <div className="relative group">
                        <img
                            src={preview}
                            alt="Preview"
                            className="w-full h-80 object-cover rounded-lg border border-stone-300"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-white text-stone-800 px-4 py-2 rounded text-sm font-medium hover:bg-stone-100"
                                    disabled={uploading}
                                >
                                    Change
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="bg-red-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-600"
                                    disabled={uploading}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                        {uploading && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                <div className="flex items-center space-x-2">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-800"></div>
                                    <span className="text-stone-800 text-base">Uploading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-80 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-stone-400 transition-colors"
                    >
                        <svg className="w-16 h-16 text-stone-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <p className="text-stone-500 text-base mb-2">Click to upload image</p>
                        <p className="text-stone-400 text-sm mt-1">PNG, JPG, WebP up to 10MB</p>
                        {enableCrop && <p className="text-stone-400 text-sm">Crop tool will appear after selection</p>}
                    </div>
                )}
            </div>

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
            />

            {/* Upload Button (Alternative) */}
            {!preview && (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full bg-stone-100 hover:bg-stone-200 text-stone-700 py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                    {uploading ? (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-700 mr-2"></div>
                            Uploading...
                        </div>
                    ) : (
                        'Choose Image'
                    )}
                </button>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                    {error}
                </div>
            )}

            {/* Preview Component */}
            {previewComponent && preview && (
                <div className="mt-4">
                    <p className="text-sm font-medium text-stone-700 mb-2">Preview (how it will look on the website):</p>
                    {previewComponent}
                </div>
            )}

            {/* Upload Instructions */}
            <p className="text-xs text-stone-500">
                Recommended: Upload high-quality images for best results. Images will be automatically optimized.
                {enableCrop && ' Use the crop tool to fit your image perfectly.'}
            </p>

            {/* Crop Modal */}
            {showCrop && (
                <ImageCrop
                    src={cropSrc}
                    aspectRatio={getAspectRatio()}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
        </div>
    );
};

export default ImageUpload;